import { Command } from 'commander';
import { confirm } from '@inquirer/prompts';
import { execSync } from 'child_process';
import { ConfigService } from '../services/config.service.js';
import { ValidationError } from '../errors/index.js';
import { trackAction } from '../services/tracking.service.js';
import packageJson from '../../package.json' with { type: 'json' };
import { disableUpdateCheck } from '../services/version-check.service.js';
import { getCliName } from '../utils/cli-name.js';

// Colors for terminal output
const colors = {
    yellow: '\x1b[33m',
    green: '\x1b[32m',
    cyan: '\x1b[36m',
    red: '\x1b[31m',
    reset: '\x1b[0m',
    bold: '\x1b[1m',
};

/**
 * Create self-update command
 */
export function createSelfUpdateCommand(): Command {
    return new Command('self-update')
        .description('Update CLI client to the latest version')
        .option('--check', 'Only check for updates without installing')
        .option('--force', 'Force update without confirmation')
        .action(async (options: { check?: boolean; force?: boolean }) => {
            await handleSelfUpdate(options);
        });
}

async function handleSelfUpdate(options: { check?: boolean; force?: boolean }) {
    const configService = new ConfigService();
    const config = await configService.load();

    if (!config) {
        throw new ValidationError(`Not initialized. Run "${getCliName()} auth" first.`);
    }

    try {
        // Fetch latest version from API
        console.log(`${colors.cyan}🔍 Checking for updates...${colors.reset}`);

        const response = await fetch(`${config.apiUrl}/api/versions/client`, {
            headers: {
                'JAI1-Access-Key': config.accessKey,
            },
            signal: AbortSignal.timeout(10000), // 10 second timeout
        });

        if (!response.ok) {
            throw new Error(`Failed to check for updates: ${response.statusText}`);
        }

        const data = await response.json() as { version: string };
        const latestVersion = data.version;
        const currentVersion = packageJson.version;

        console.log(`\n${colors.bold}Current version:${colors.reset} ${currentVersion}`);
        console.log(`${colors.bold}Latest version:${colors.reset}  ${latestVersion}\n`);

        if (!isNewerVersion(latestVersion, currentVersion)) {
            console.log(`${colors.green}✅ You're already on the latest version!${colors.reset}\n`);
            return;
        }

        // Show update available
        console.log(`${colors.yellow}╭──────────────────────────────────────────────────────────╮${colors.reset}`);
        console.log(`${colors.yellow}│${colors.reset}  ${colors.bold}⬆️  Update available!${colors.reset} ${currentVersion} → ${colors.cyan}${latestVersion}${colors.reset}                  ${colors.yellow}│${colors.reset}`);
        console.log(`${colors.yellow}╰──────────────────────────────────────────────────────────╯${colors.reset}\n`);

        // If --check only, stop here
        if (options.check) {
            console.log(`${colors.cyan}Run "${getCliName()} self-update" to install the latest version.${colors.reset}\n`);
            return;
        }

        // Ask for confirmation unless --force
        if (!options.force) {
            const shouldUpdate = await confirm({
                message: 'Update to the latest version now?',
                default: true
            });

            if (!shouldUpdate) {
                console.log(`${colors.yellow}⏸️  Update cancelled.${colors.reset}\n`);
                return;
            }
        }

        // Perform update
        console.log(`\n${colors.cyan}📥 Installing latest version...${colors.reset}\n`);

        try {
            // Detect package manager (npm, pnpm, yarn, bun)
            const packageManager = detectPackageManager();
            const installCommand = getInstallCommand(packageManager);

            console.log(`${colors.cyan}Using ${packageManager}...${colors.reset}`);

            // Execute installation
            execSync(installCommand, {
                stdio: 'inherit',
                env: { ...process.env, FORCE_COLOR: '1' }
            });

            console.log(`\n${colors.green}✅ Successfully updated to version ${latestVersion}!${colors.reset}\n`);

            // Track successful update
            trackAction('self_update', {
                from_version: currentVersion,
                to_version: latestVersion,
                package_manager: packageManager
            });

            // Disable update check for this session since we just upgraded
            disableUpdateCheck();

        } catch (error) {
            console.error(`\n${colors.red}❌ Update failed!${colors.reset}`);
            console.error(`${colors.red}Error: ${error instanceof Error ? error.message : 'Unknown error'}${colors.reset}\n`);
            console.error(`${colors.yellow}💡 You can try manually updating with:${colors.reset}`);
            console.error(`   ${colors.cyan}npm install -g @jvittechs/j@latest${colors.reset}\n`);
            throw error;
        }

    } catch (error) {
        if (error instanceof Error && error.message.includes('Failed to check')) {
            throw new ValidationError('Cannot connect to update server. Please check your internet connection.');
        }
        throw error;
    }
}

/**
 * Compare versions to check if remote is newer than local
 */
function isNewerVersion(remote: string, local: string): boolean {
    const remoteParts = remote.split('.').map(Number);
    const localParts = local.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
        const r = remoteParts[i] || 0;
        const l = localParts[i] || 0;

        if (r > l) return true;
        if (r < l) return false;
    }

    return false;
}

/**
 * Detect which package manager is being used
 */
function detectPackageManager(): 'npm' | 'pnpm' | 'yarn' | 'bun' {
    // Check user agent for package manager
    const userAgent = process.env.npm_config_user_agent || '';

    if (userAgent.includes('pnpm')) return 'pnpm';
    if (userAgent.includes('yarn')) return 'yarn';
    if (userAgent.includes('bun')) return 'bun';

    // Check which command is available
    try {
        execSync('pnpm --version', { stdio: 'ignore' });
        return 'pnpm';
    } catch {
        // pnpm not available
    }

    try {
        execSync('yarn --version', { stdio: 'ignore' });
        return 'yarn';
    } catch {
        // yarn not available
    }

    try {
        execSync('bun --version', { stdio: 'ignore' });
        return 'bun';
    } catch {
        // bun not available
    }

    // Default to npm
    return 'npm';
}

/**
 * Get install command for package manager
 */
function getInstallCommand(packageManager: 'npm' | 'pnpm' | 'yarn' | 'bun'): string {
    const packageName = '@jvittechs/j@latest';

    switch (packageManager) {
        case 'pnpm':
            return `pnpm add -g ${packageName}`;
        case 'yarn':
            return `yarn global add ${packageName}`;
        case 'bun':
            return `bun add -g ${packageName}`;
        case 'npm':
        default:
            return `npm install -g ${packageName}`;
    }
}
